/**
 * Infinite Scroll per collezioni Shopify
 * - Carica 50 prodotti iniziali
 * - Pre-carica i prossimi 50 al 50% dello scroll
 * - Mostra spinner quando arriva in fondo e sta caricando
 */
(() => {
  const getSectionIds = () => {
    if (Array.isArray(window.__ALYSI_INFINITE_SECTIONS__) && window.__ALYSI_INFINITE_SECTIONS__.length) {
      return window.__ALYSI_INFINITE_SECTIONS__;
    }
    return ['main-collection-product-grid', 'main-collection', 'collection-products', 'collection-grid', 'products'];
  };

  const cfg = {
    sectionIds: getSectionIds(),
    gridSelectors: [
      '#ProductGrid', '#product-grid', '#product-grid-ajax', '#AjaxinateContainer', '#CollectionProductGrid', '#collection-product-grid',
      '[data-products-grid]', '[data-section-id="main-collection-product-grid"]',
      '.collection .product-grid', '.collection-grid', '.grid--view-items .grid', '[data-section-type="collection"] .grid',
      'ul.grid.product-grid', 'div.grid.product-grid', '#MainContent .product-grid', '#MainContent [data-products-grid]',
      '.grid.grid--products'
    ],
    paginationSelectors: [
      '.pagination', 'ul.pagination', '.pagination__list', '.paginate', '#pagination', '#pagination-ajax', '.pagination-main',
      'nav[role="navigation"]', 'nav[aria-label*="Pagination"]', 'nav[aria-label*="Pagina"]', '[data-pagination]',
      '.collection__pagination', '.page-pagination', '.pagination-wrapper', '.product-grid-paginator', '.list--pagination', '.collection-pagination'
    ],
    prefetchThreshold: 0.5, // Pre-carica al 50% dello scroll
    showThreshold: 0.9,     // Mostra i prodotti al 90% (quasi in fondo)
    showSpinner: true,
    debug: false
  };

  const isDesignMode = () => {
    try { return !!(window.Shopify && Shopify.designMode) || document.body.classList.contains('shopify-design-mode'); } catch { return false; }
  };

  const ready = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };

  const log = (...args) => { if (cfg.debug) console.log('[InfiniteScroll]', ...args); };

  // Nasconde la paginazione nativa
  const hidePagination = () => {
    const styleHide = document.getElementById('infinite-hide-style') || document.createElement('style');
    styleHide.id = 'infinite-hide-style';
    styleHide.textContent = `
      .pagination, ul.pagination, .pagination__list, .paginate, #pagination, #pagination-ajax, .pagination-main,
      nav[role="navigation"], nav[aria-label*="Pagination"], nav[aria-label*="Pagina"], [data-pagination],
      .collection__pagination, .page-pagination, .pagination-wrapper, .product-grid-paginator, .list--pagination, .collection-pagination { display: none !important; }
      a[href*="?page"], a[href*="&page"], a[rel="next"] { display: none !important; }
    `;
    if (!styleHide.parentNode) document.head.appendChild(styleHide);
    cfg.paginationSelectors.forEach(sel => { 
      document.querySelectorAll(sel).forEach(p => { p.style.display = 'none'; }); 
    });
  };

  // Trova la griglia prodotti
  const findGrid = (root = document) => {
    for (const sel of cfg.gridSelectors) {
      const el = root.querySelector(sel);
      if (el) {
        // Alcuni selettori (es. #product-grid in search) matchano un wrapper: prova a prendere la griglia reale al suo interno
        const nestedGrid = el.querySelector?.('.grid.grid--products, ul.grid, .product-grid');
        if (nestedGrid) return nestedGrid;
        return el;
      }
    }
    const cards = root.querySelectorAll('[id*="CardProduct"], [id*="ProductCard"], .product-card, [data-product-card], article.product, li.grid__item');
    if (cards.length) {
      const parent = cards[0].closest('.grid, .product-grid, .collection-grid, [role="list"], ul, ol');
      if (parent) return parent;
    }
    return null;
  };

  // Costruisce URL per la pagina
  const buildUrl = (page, sectionId) => {
    const u = new URL(window.location.href);
    u.searchParams.set('page', String(page));
    if (sectionId) u.searchParams.set('section_id', sectionId);
    return u.toString();
  };

  const parseHTML = (html) => (new DOMParser()).parseFromString(html, 'text/html');

  const isProductCard = (el) =>
    el.classList.contains('card--product') ||
    el.classList.contains('card--article') ||
    el.classList.contains('card') && el.querySelector('a[href*="/products/"], a[href*="/articles/"]') ||
    el.querySelector?.('.card--product') ||
    el.querySelector?.('[id*="CardProduct"]');

  const extractProducts = (doc) => {
    const grid = findGrid(doc);
    if (grid) {
      const children = Array.from(grid.children).filter(isProductCard);
      if (children.length > 0) return children;
    }
    const cards = doc.querySelectorAll('.card--product, .card--article, [id*="CardProduct"], [data-product-card]');
    return Array.from(cards);
  };

  // Verifica se ci sono altre pagine
  const getNextPageNumber = (doc, currentPage) => {
    // 1) Full page: <link rel="next" ...>
    const relNext = doc.querySelector('a[rel="next"], link[rel="next"]');
    if (relNext) {
      const href = relNext.getAttribute('href');
      if (!href) return currentPage + 1;
      try {
        const u = new URL(href, window.location.origin);
        const p = parseInt(u.searchParams.get('page') || String(currentPage + 1), 10);
        return Number.isFinite(p) ? p : (currentPage + 1);
      } catch {
        return currentPage + 1;
      }
    }

    // 2) Section render: usa i link della paginazione numerica
    const links = Array.from(doc.querySelectorAll('#pagination-ajax a[href], nav.pagination a[href], .pagination__list a[href]'));
    let next = null;
    for (const a of links) {
      const href = a.getAttribute('href');
      if (!href) continue;
      try {
        const u = new URL(href, window.location.origin);
        const p = parseInt(u.searchParams.get('page') || '0', 10);
        if (Number.isFinite(p) && p > currentPage) {
          if (next === null || p < next) next = p;
        }
      } catch {
        // ignore invalid urls
      }
    }
    return next;
  };

  const hasNextPage = (doc, currentPage) => getNextPageNumber(doc, currentPage) !== null;

  ready(() => {
    hidePagination();

    const url = new URL(window.location.href);
    let currentPage = parseInt(url.searchParams.get('page') || '1', 10);
    
    // Stati
    let prefetchedProducts = null;  // Prodotti pre-caricati
    let prefetchedPage = null;      // Pagina pre-caricata
    let isPrefetching = false;      // Sta pre-caricando?
    let isAppending = false;        // Sta appendendo?
    let done = false;               // Finite le pagine?
    let hasMorePages = true;        // Ci sono altre pagine?

    const grid = findGrid();
    if (!grid) {
      log('Grid container non trovato');
      return;
    }

    log('Grid trovata:', grid);

    // Crea spinner
    let spinner = null;
    const createSpinner = () => {
      if (spinner) return spinner;
      spinner = document.createElement('div');
      spinner.setAttribute('id', 'infinite-spinner');
      spinner.innerHTML = `
        <div style="display:flex;justify-content:center;align-items:center;padding:40px 0;">
          <div style="width:40px;height:40px;border-radius:50%;border:3px solid #e0e0e0;border-top-color:#333;animation:infinite-spin 0.8s linear infinite;"></div>
        </div>
      `;
      const style = document.createElement('style');
      style.textContent = '@keyframes infinite-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
      return spinner;
    };

    const showSpinner = () => {
      if (!cfg.showSpinner) return;
      const s = createSpinner();
      if (!s.parentNode) {
        grid.parentNode.insertBefore(s, grid.nextSibling);
      }
    };

    const hideSpinner = () => {
      if (spinner && spinner.parentNode) {
        spinner.remove();
      }
    };

    // Pre-carica la prossima pagina in background
    const prefetchNextPage = async () => {
      if (isPrefetching || done || prefetchedProducts) return;
      
      const nextPage = currentPage + 1;
      isPrefetching = true;
      log('Pre-caricamento pagina', nextPage);

      for (const sid of cfg.sectionIds) {
        try {
          const res = await fetch(buildUrl(nextPage, sid), { credentials: 'include' });
          if (res.ok) {
            const html = await res.text();
            const doc = parseHTML(html);
            const products = extractProducts(doc);

            if (products.length > 0) {
              prefetchedProducts = products;
              prefetchedPage = nextPage;
              hasMorePages = hasNextPage(doc, nextPage);
              log('Pre-caricati', products.length, 'elementi dalla pagina', nextPage, '- altre pagine:', hasMorePages);
              isPrefetching = false;
              return;
            } else {
              done = true;
              hideSpinner();
              log('Nessun prodotto nella pagina', nextPage, '(section', sid, ') - fine');
              isPrefetching = false;
              return;
            }
          }
        } catch (e) {
          log('Errore pre-fetch section', sid, e);
        }
      }

      done = true;
      hideSpinner();
      isPrefetching = false;
    };

    // Appende i prodotti pre-caricati alla griglia
    const appendPrefetchedProducts = () => {
      if (isAppending || !prefetchedProducts || prefetchedProducts.length === 0) return false;
      
      isAppending = true;
      log('Appendo', prefetchedProducts.length, 'prodotti');

      prefetchedProducts.forEach(product => {
        const clone = product.cloneNode(true);
        grid.appendChild(clone);
      });

      currentPage = prefetchedPage;
      prefetchedProducts = null;
      prefetchedPage = null;

      // Aggiorna URL senza ricaricare
      const u = new URL(window.location.href);
      u.searchParams.set('page', String(currentPage));
      history.replaceState({}, '', u.toString());

      hidePagination();
      isAppending = false;

      // Se non ci sono più pagine, marca come done e nascondi spinner
      if (!hasMorePages) {
        done = true;
        hideSpinner();
        log('Ultima pagina raggiunta, spinner nascosto');
      }

      return true;
    };

    // Calcola la percentuale di scroll
    const getScrollPercent = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return 1;
      return scrollTop / scrollHeight;
    };

    // Handler dello scroll
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      
      ticking = true;
      requestAnimationFrame(() => {
        const scrollPercent = getScrollPercent();
        
        // Al 50%: inizia pre-caricamento
        if (scrollPercent >= cfg.prefetchThreshold && !prefetchedProducts && !isPrefetching && !done) {
          prefetchNextPage();
        }

        // Al 90% (quasi in fondo): mostra i prodotti
        if (scrollPercent >= cfg.showThreshold && !done) {
          if (prefetchedProducts && prefetchedProducts.length > 0) {
            // Prodotti pronti: appendili
            hideSpinner();
            appendPrefetchedProducts();
            // Inizia subito il pre-fetch della prossima pagina
            if (!done) {
              prefetchNextPage();
            }
          } else if (isPrefetching) {
            // Ancora in caricamento: mostra spinner
            showSpinner();
          } else if (!isPrefetching && !prefetchedProducts && !done) {
            // Niente in cache e non sta caricando: avvia caricamento
            showSpinner();
            prefetchNextPage();
          }
        }

        // Nascondi spinner se i prodotti sono stati caricati o se abbiamo finito
        if ((prefetchedProducts || done) && spinner && spinner.parentNode) {
          hideSpinner();
        }

        ticking = false;
      });
    };

    // Observer per quando i prodotti vengono appendati
    const observer = new MutationObserver(() => {
      hidePagination();
    });
    observer.observe(grid, { childList: true, subtree: true });

    // Ascolta scroll
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Trigger iniziale per pagine corte
    setTimeout(() => {
      handleScroll();
      // Se la pagina è corta, potrebbe servire caricare subito
      if (getScrollPercent() >= cfg.prefetchThreshold) {
        prefetchNextPage();
      }
    }, 500);

    // Design mode: controlla periodicamente
    if (isDesignMode()) {
      setInterval(handleScroll, 1000);
    }

    // Esponi API globale
    window.ALYSI_InfiniteScroll = { 
      hidePagination, 
      prefetchNextPage,
      appendPrefetchedProducts,
      getState: () => ({ currentPage, done, isPrefetching, hasPrefetched: !!prefetchedProducts })
    };

    log('Infinite scroll inizializzato');
  });
})();
